/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 *
 *
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.0.0
 */

import type { FC } from 'react';

import Highlighter from 'react-highlight-words';
import { useDispatch } from 'react-redux';

import type { ILabelEditorTranslation } from '@/main/modules/internationalization/types/corporate';
import type { IModalRenderProps } from '@/main/modules/modal/types/modalTypes';
import { icons } from '@assets/index';
import { Checkbox, EllipseText } from '@components/common';
import { ImgStyled } from '@components/common/Block/Img';
import { ILabelColor } from '@components/Table/constants/labelColor';
import { modalOpen } from '@modules/modal/modalActions';
import ServiceUserDenied from '@modules/role/components/ServiceUserDenied';

import type { IEditableLabel } from '../labelEditorTypes';
import LabelEditorRemoveModal from './LabelEditorRemoveModal';
import { LabelEditorColorStyled, LabelEditorItemStyled, LabelEditorNameStyled, LabelEditorRemoveItemStyled } from './styled/LabelEditorListStyled';
interface ILabelEditorItemProps {
    label: IEditableLabel;
    switchLabel?: (label: IEditableLabel) => void;
    searchValue: string;
    removeLabel?: (id: number) => void;
    translation: ILabelEditorTranslation;
};

export const LabelEditorItem: FC<ILabelEditorItemProps> = (
    {
        label,
        switchLabel,
        searchValue,
        removeLabel,
        translation,
    },
) => {
    const dispatch = useDispatch();

    const { id, name, color, checked } = label;

    const HandleOnCheck = (value: boolean) => {
        if (switchLabel) {
            switchLabel({ ...label, checked: value })
        }
    };

    const handleRemoveLabel = () => {
        dispatch(modalOpen({
            render: (p: IModalRenderProps) => <LabelEditorRemoveModal
                handleClose={p.onCloseModal}
                handleSubmit={() => {
                    p.onCloseModal();
                    if (removeLabel) {
                        removeLabel(id);
                    }
                }}
                name={name}
                translation={translation}
            />,
        }));
    };

    return (
        <LabelEditorItemStyled className="label-editor__item" data-label-id={id} key={id}>
            {switchLabel && <Checkbox
                checked={checked}
                className="label-editor__item-checkbox"
                onChange={HandleOnCheck}
            />}
            {color && <LabelEditorColorStyled color={`color${color}` as ILabelColor} />}
            <LabelEditorNameStyled className="label-editor__item-name">
                <EllipseText title={name}>
                    <Highlighter
                        autoEscape={true}
                        highlightClassName="label-editor__item-text_bold"
                        searchWords={[searchValue]}
                        textToHighlight={name}/>
                </EllipseText>
            </LabelEditorNameStyled>
            {removeLabel && (
                <ServiceUserDenied renderComponent={disabled =>
                    <LabelEditorRemoveItemStyled
                        data-name='removeLabel'
                        disabled={disabled}
                        role='figure'
                        onClick={handleRemoveLabel}>
                        <ImgStyled
                            src={disabled ? icons.removeTinyOpacityIcon : icons.removeTinyIcon}
                            width="16px"
                        />
                    </LabelEditorRemoveItemStyled>
                }/>
            )}
        </LabelEditorItemStyled>
    )
};
